import { z } from 'zod';
import { autorizacionesAdjuntas } from './autorizaciones.ts';
import { decimalNoNegativo, decimalPositivo, email, identificacionCliente, paginacion, uuid } from './comun.ts';

/** Plazo normal de crédito en días (config `credito_plazo_normal_dias`); por encima, la dueña recibe aviso. */
export const PLAZO_CREDITO_NORMAL = 20;

/** Teléfono de contacto: obligatorio para registrar un cliente (la nota de entrega lo exige). */
const telefono = z
  .string({ error: 'Indica un teléfono de contacto' })
  .trim()
  .max(40)
  .refine((v) => v.replace(/\D/g, '').length >= 7, 'Indica un teléfono de contacto (mínimo 7 dígitos)');

/** Alta/edición BÁSICA (cajero+): datos fiscales y de contacto. Nace con lista default + contado (CLI-02). */
export const clienteBasicoInput = z.object({
  rif: identificacionCliente,
  razon_social: z.string().trim().min(2, 'Mínimo 2 caracteres').max(160),
  direccion_fiscal: z.string().trim().max(300).default(''),
  telefono,
  correo: email.nullable().default(null),
  notas: z.string().trim().max(1000).nullable().default(null),
});
export type ClienteBasicoInput = z.infer<typeof clienteBasicoInput>;
export type ClienteBasicoEntrada = z.input<typeof clienteBasicoInput>;

/**
 * Alta de cliente: datos básicos + clasificación fiscal inicial (especial / municipal). Pedido del cliente
 * 24-09-2026: se marca al incluirlo. Cambiarla DESPUÉS sigue siendo solo de admin (clienteCreditoInput).
 */
export const clienteCrearInput = clienteBasicoInput.extend({
  tipo_contribuyente: z.enum(['ordinario', 'especial']).default('ordinario'),
  municipal: z.boolean().default(false),
  /** % municipal propio; null = usar el global (config retencion_municipal_pct). */
  retencion_municipal_pct: decimalNoNegativo.refine((v) => Number(v) <= 100, 'El % no puede superar 100').nullable().default(null),
});
export type ClienteCrearInput = z.infer<typeof clienteCrearInput>;
export type ClienteCrearEntrada = z.input<typeof clienteCrearInput>;

/** Edición PARCIAL: sin los `.default()` del alta (cambiar solo el teléfono borraba dirección, correo y notas). */
export const clienteEditarBasicoInput = clienteBasicoInput.partial().extend({
  direccion_fiscal: z.string().trim().max(300).optional(),
  correo: email.nullable().optional(),
  notas: z.string().trim().max(1000).nullable().optional(),
});
export type ClienteEditarBasicoInput = z.infer<typeof clienteEditarBasicoInput>;

/** Clasificación, lista y crédito: SOLO admin_sucursal+ (spec 03 reglas 3-5). */
export const clienteCreditoInput = z
  .object({
    tipo_contribuyente: z.enum(['ordinario', 'especial']).optional(),
    municipal: z.boolean().optional(),
    /** % municipal propio; null = usar el global (config retencion_municipal_pct). */
    retencion_municipal_pct: decimalNoNegativo.nullable().optional(),
    lista_id: uuid.optional(),
    condicion: z.enum(['contado', 'credito']).optional(),
    limite_credito_usd: decimalNoNegativo.optional(),
    /** Días de plazo (lo normal son 20); 0 solo al volver a contado. */
    plazo_dias: z.number().int().min(0).max(365).optional(),
    activo: z.boolean().optional(),
  })
  .refine((d) => Object.keys(d).length > 0, 'Nada que actualizar');
export type ClienteCreditoInput = z.infer<typeof clienteCreditoInput>;

export const clientesFiltros = paginacion.extend({
  q: z.string().trim().max(120).optional(),
  tipo: z.enum(['ordinario', 'especial']).optional(),
  condicion: z.enum(['contado', 'credito']).optional(),
  activo: z
    .enum(['true', 'false', 'todos'])
    .default('true')
    .transform((v) => (v === 'todos' ? undefined : v === 'true')),
});
export type ClientesFiltros = z.infer<typeof clientesFiltros>;

export interface ClienteDTO {
  id: string;
  rif: string;
  razon_social: string;
  direccion_fiscal: string;
  telefono: string | null;
  correo: string | null;
  tipo_contribuyente: 'ordinario' | 'especial';
  municipal: boolean;
  retencion_municipal_pct: string | null;
  lista_id: string;
  lista_codigo: string;
  lista_nombre: string;
  condicion: 'contado' | 'credito';
  limite_credito_usd: string;
  plazo_dias: number;
  saldo_a_favor: string;
  /**
   * Parte del saldo a favor que entró en divisas sin IGTF (paga el 3 % al usarse en una factura o nota de débito). Solo
   * la ficha (GET /clientes/:id y el estado de cuenta) la trae, y solo a la dueña y al contador; a los demás, null.
   */
  saldo_divisa_sin_igtf?: string | null;
  consumidor_final: boolean;
  activo: boolean;
  notas: string | null;
  /** ¿Puede emitirse FACTURA? RIF y dirección fiscal presentes (spec 03 regla 1). */
  apto_factura: boolean;
  /** ¿Puede emitirse NOTA de entrega? Cédula/RIF y teléfono presentes (respuesta de la clienta 2026-10-09). */
  apto_nota: boolean;
  creado_en: string;
  actualizado_en: string;
}

/** Movimientos del saldo a favor (CLI-03). traspaso_* y ajuste: spec 03, enmienda 2026-10-29. */
export const TIPOS_MOVIMIENTO_SALDO = ['abono', 'devolucion', 'reintegro', 'uso_pago', 'traspaso_salida', 'traspaso_entrada', 'ajuste'] as const;
export type TipoMovimientoSaldo = (typeof TIPOS_MOVIMIENTO_SALDO)[number];

export interface MovimientoSaldoDTO {
  id: number;
  ocurrido_en: string;
  tipo: TipoMovimientoSaldo;
  monto: string;
  saldo_antes: string;
  saldo_despues: string;
  referencia_tipo: string;
  referencia_id: string;
  usuario_nombre: string | null;
  detalle: unknown;
  /** Número de la nota o factura del movimiento (si la referencia es un documento). */
  documento_numero?: string | null;
  /** Sucursal donde ocurrió (para saber si su documento se puede abrir desde la sucursal que se opera). */
  sucursal_nombre?: string | null;
  /**
   * Lo que el movimiento sumó (+) o restó (−) a la parte del saldo en divisas sin IGTF, y cómo quedó esa parte después.
   * Solo la dueña y el contador lo ven; a los demás, null.
   */
  divisa_sin_igtf?: string | null;
  divisa_sin_igtf_despues?: string | null;
  /** Traspaso: el otro cliente (a quien se le pasó o de quien vino); ajuste y los demás: null. */
  contraparte?: { cliente_id: string; razon_social: string; rif: string } | null;
}

/**
 * «Traspasar saldo» (spec 03, enmienda 2026-10-29): solo el saldo que dejó una nota de crédito (o un Cambio) de este
 * cliente, a otro cliente, con motivo. Dos movimientos enlazados; la parte en divisas sin IGTF viaja en proporción.
 */
export const traspasoSaldoInput = z.object({
  cliente_destino_id: uuid,
  /** La nota de crédito (o el Cambio) de este cliente que dejó el saldo que se pasa. */
  nota_credito_id: uuid,
  monto: decimalPositivo,
  motivo: z.string().trim().min(3, 'Indica el motivo').max(300),
  autorizaciones: autorizacionesAdjuntas,
});
export type TraspasoSaldoInput = z.infer<typeof traspasoSaldoInput>;

/**
 * «Ajustar saldo» (spec 03, enmienda 2026-10-29): baja el saldo a favor de un cliente (p. ej. un pago registrado dos
 * veces), con el documento donde de verdad se usó ese dinero y el motivo. No mueve caja.
 */
export const ajusteSaldoInput = z.object({
  /** Factura, nota o nota de débito del mismo cliente donde se usó el dinero. */
  documento_id: uuid,
  monto: decimalPositivo,
  motivo: z.string().trim().min(3, 'Indica el motivo').max(300),
  autorizaciones: autorizacionesAdjuntas,
});
export type AjusteSaldoInput = z.infer<typeof ajusteSaldoInput>;

/** Nota de crédito (o Cambio) del cliente con lo que todavía se puede traspasar de su crédito. */
export interface NotaCreditoSaldoDTO {
  id: string;
  tipo: 'NC' | 'CAM';
  numero_formateado: string;
  emitido_en: string;
  documento_origen_numero: string | null;
  /** Lo que la nota acreditó al saldo de este cliente. */
  credito: string;
  /** Lo que ya se pasó a otros clientes desde esta nota. */
  traspasado: string;
  /** Lo que todavía se puede pasar: el crédito menos lo traspasado, sin pasar del saldo actual del cliente. */
  disponible: string;
}

/** Resultado de un traspaso o un ajuste de saldo. */
export interface OperacionSaldoDTO {
  /** La operación (enlaza los dos movimientos del traspaso). */
  id: string;
  tipo: 'traspaso' | 'ajuste';
  monto: string;
  cliente: { id: string; razon_social: string; saldo_antes: string; saldo_despues: string };
  /** Traspaso: el cliente que recibe. */
  destino: { id: string; razon_social: string; saldo_antes: string; saldo_despues: string } | null;
  /** Nota de crédito (traspaso) o documento donde se usó el dinero (ajuste). */
  referencia: { id: string; tipo: string; numero_formateado: string };
  motivo: string;
}

/** Documento a crédito con saldo: la cuenta por cobrar del cliente, con su vencimiento. */
export interface CxcDocumentoDTO {
  id: string;
  tipo: string;
  numero_formateado: string;
  emitido_en: string;
  vence_en: string | null;
  total: string;
  saldo_pendiente: string;
  /** Días de atraso (0 si aún no vence). */
  dias_vencido: number;
  /** Sucursal que lo emitió: la ficha solo enlaza los documentos de la sucursal que se está operando. */
  sucursal_nombre: string;
}

export interface EstadoCuentaDTO {
  cliente: ClienteDTO;
  movimientos_saldo: MovimientoSaldoDTO[];
  /** Cuentas por cobrar: documentos a crédito con saldo, del más antiguo al más reciente. */
  cxc: { total: string; vencido: string; documentos: CxcDocumentoDTO[] };
  credito_disponible: string;
  /** Expediente (CLI-08): últimos documentos, cotizaciones y abonos del cliente. */
  documentos: { id: string; tipo: string; numero_formateado: string; emitido_en: string; total: string; saldo_pendiente: string; estado_pago: string; estado: 'emitido' | 'anulado'; sucursal_nombre: string }[];
  cotizaciones: { id: string; numero_formateado: string; creado_en: string; total: string; estado: string }[];
  abonos: { id: string; numero_formateado: string | null; recibido_en: string; total_usd: string }[];
  /** Últimos pagos recibidos del cliente (cobros de documentos, abonos y reintegros), del más reciente al más antiguo. */
  pagos: { id: string; tipo: 'cobro' | 'abono' | 'reintegro'; numero_formateado: string | null; recibido_en: string; total_usd: string; metodos: string[]; documento_id: string | null; documento: string | null; sucursal_nombre: string }[];
}
